<template>
  <div class="builder-group">
    <div class="builder-group-header">
      <el-button size="small" @click="toggleCombinator(group)">{{
        group.combinator
      }}</el-button>
      <el-button size="small" @click="addRule(group)">ルールを追加</el-button>
      <el-button size="small" @click="addGroup(group)">
        グループを追加
      </el-button>
      <el-button
        v-if="parentGroup && typeof groupIndex === 'number'"
        type="danger"
        size="small"
        plain
        circle
        style="margin-left: auto"
        @click="deleteGroup(parentGroup, groupIndex)"
      >
        <el-icon><DeleteFilled /></el-icon>
      </el-button>
    </div>
    <div class="builder-rules" v-for="(rule, idx) in group.rules" :key="idx">
      <div v-if="'rules' in rule" class="builder-nested-group">
        <DynamicBuilder
          :group="rule"
          :parent-group="group"
          :group-index="idx"
          :form-ref="formRef"
          :form-prefix="`${formPrefix}.rules.${idx}`"
        />
      </div>
      <div v-else class="builder-rule">
        <el-form-item
          :prop="`${formPrefix}.rules.${idx}.field`"
          :rules="[
            {
              required: true,
              message: 'フィールドは必須です',
              trigger: 'change'
            }
          ]"
        >
          <el-select
            v-model="rule.field"
            filterable
            placeholder="フィールド"
            size="small"
            style="width: 200px"
          >
            <el-option
              v-for="opt in fieldOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          :prop="`${formPrefix}.rules.${idx}.operator`"
          :rules="[
            {
              required: true,
              message: '演算子は必須です',
              trigger: 'change'
            }
          ]"
        >
          <el-select v-model="rule.operator" placeholder="演算子" size="small">
            <el-option label="等しい" value="equal" />
            <el-option label="等しくない" value="not_equal" />
            <el-option label="より大きい" value="greater" />
            <el-option label="以上" value="greater_equal" />
            <el-option label="より小さい" value="less" />
            <el-option label="以下" value="less_equal" />
            <el-option label="含む" value="contains" />
            <el-option label="含まない" value="not_contains" />
            <el-option label="で始まる" value="starts_with" />
            <el-option label="で終わる" value="ends_with" />
          </el-select>
        </el-form-item>
        <el-form-item
          :prop="`${formPrefix}.rules.${idx}.value`"
          :rules="[
            { required: true, message: '値は必須です', trigger: 'blur' }
          ]"
        >
          <el-input
            v-model="rule.value"
            placeholder="値"
            size="small"
            style="width: auto"
          />
        </el-form-item>
        <el-button
          type="danger"
          size="small"
          plain
          circle
          style="margin-left: auto"
          @click="deleteRuleOrGroup(group, idx)"
        >
          <el-icon><DeleteFilled /></el-icon>
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { DeleteFilled } from '@element-plus/icons-vue';
  import type { FormInstance } from 'element-plus';

  const fieldOptions = [
    { label: 'deviceLedger.id', value: 'deviceLedger.id' },
    { label: 'deviceLedger.financeId', value: 'deviceLedger.financeId' },
    { label: 'deviceLedger.deviceId', value: 'deviceLedger.deviceId' },
    { label: 'deviceLedger.deviceCode', value: 'deviceLedger.deviceCode' },
    { label: 'deviceLedger.deviceType', value: 'deviceLedger.deviceType' },
    { label: 'deviceLedger.serialCode', value: 'deviceLedger.serialCode' },
    { label: 'deviceLedger.simNumber', value: 'deviceLedger.simNumber' },
    { label: 'deviceLedger.simImsi', value: 'deviceLedger.simImsi' },
    { label: 'deviceLedger.firmVersion', value: 'deviceLedger.firmVersion' },
    {
      label: 'deviceLedger.deviceSettingUser',
      value: 'deviceLedger.deviceSettingUser'
    },
    {
      label: 'deviceLedger.deviceSettingPassword',
      value: 'deviceLedger.deviceSettingPassword'
    },
    { label: 'deviceLedger.status', value: 'deviceLedger.status' },
    { label: 'deviceLedger.mboot', value: 'deviceLedger.mboot' },
    {
      label: 'deviceLedger.internalBatteryVoltage',
      value: 'deviceLedger.internalBatteryVoltage'
    },
    { label: 'deviceLedger.acceptId', value: 'deviceLedger.acceptId' },
    { label: 'deviceLedger.shipId', value: 'deviceLedger.shipId' },
    {
      label: 'deviceLedger.orderCompanyId',
      value: 'deviceLedger.orderCompanyId'
    },
    { label: 'deviceLedger.note', value: 'deviceLedger.note' },
    { label: 'deviceLedger.isDeleted', value: 'deviceLedger.isDeleted' },
    { label: 'deviceLedger.iccid', value: 'deviceLedger.iccid' },
    {
      label: 'deviceLedger.handlingType',
      value: 'deviceLedger.handlingType'
    },
    {
      label: 'deviceLedger.registerUserId',
      value: 'deviceLedger.registerUserId'
    },
    {
      label: 'deviceLedger.updateUserId',
      value: 'deviceLedger.updateUserId'
    },
    { label: 'deviceLedger.createdAt', value: 'deviceLedger.createdAt' },
    { label: 'deviceLedger.updatedAt', value: 'deviceLedger.updatedAt' },
    {
      label: 'deviceLedger.autobacsOrderNumber',
      value: 'deviceLedger.autobacsOrderNumber'
    },
    {
      label: 'deviceLedger.deviceMinorVersion',
      value: 'deviceLedger.deviceMinorVersion'
    },
    { label: 'deviceLedger.itemId', value: 'deviceLedger.itemId' },
    { label: 'deviceLedger.configName', value: 'deviceLedger.configName' },
    { label: 'deviceLedger.IMEI', value: 'deviceLedger.IMEI' },
    { label: 'deviceLedger.IMEI2', value: 'deviceLedger.IMEI2' },
    {
      label: 'deviceLedger.labelPrintCount',
      value: 'deviceLedger.labelPrintCount'
    },
    { label: 'ship.id', value: 'ship.id' },
    { label: 'ship.shipDate', value: 'ship.shipDate' },
    { label: 'ship.depotId', value: 'ship.depotId' },
    { label: 'ship.acceptId', value: 'ship.acceptId' },
    { label: 'ship.financeId', value: 'ship.financeId' },
    { label: 'ship.financeCode', value: 'ship.financeCode' },
    { label: 'ship.customerCode', value: 'ship.customerCode' },
    { label: 'ship.type', value: 'ship.type' },
    { label: 'ship.harnessId', value: 'ship.harnessId' },
    { label: 'ship.isNewCar', value: 'ship.isNewCar' },
    { label: 'ship.firstRegistYear', value: 'ship.firstRegistYear' },
    { label: 'ship.vin', value: 'ship.vin' },
    { label: 'ship.ignitionType', value: 'ship.ignitionType' },
    { label: 'ship.carNote', value: 'ship.carNote' },
    { label: 'ship.arrivalDate', value: 'ship.arrivalDate' },
    { label: 'ship.shipZip', value: 'ship.shipZip' },
    { label: 'ship.shipPrefecture', value: 'ship.shipPrefecture' },
    { label: 'ship.shipCity', value: 'ship.shipCity' },
    { label: 'ship.shipAddress', value: 'ship.shipAddress' },
    { label: 'ship.shipAddressSub', value: 'ship.shipAddressSub' },
    { label: 'ship.shipTel', value: 'ship.shipTel' },
    { label: 'ship.shipFax', value: 'ship.shipFax' },
    { label: 'ship.shipEmail', value: 'ship.shipEmail' },
    { label: 'ship.shipName', value: 'ship.shipName' },
    { label: 'ship.shipStaffName', value: 'ship.shipStaffName' },
    { label: 'ship.shipTargetName', value: 'ship.shipTargetName' },
    { label: 'ship.note', value: 'ship.note' },
    { label: 'ship.status', value: 'ship.status' },
    { label: 'ship.registerUserId', value: 'ship.registerUserId' },
    { label: 'ship.updateUserId', value: 'ship.updateUserId' },
    { label: 'ship.carMakerId', value: 'ship.carMakerId' },
    { label: 'ship.carType', value: 'ship.carType' },
    { label: 'ship.carModel', value: 'ship.carModel' },
    { label: 'ship.isSale', value: 'ship.isSale' },
    { label: 'ship.isDeleted', value: 'ship.isDeleted' },
    { label: 'ship.createdAt', value: 'ship.createdAt' },
    { label: 'ship.updatedAt', value: 'ship.updatedAt' },
    { label: 'ship.orderCompanyId', value: 'ship.orderCompanyId' },
    { label: 'ship.invoiceCode', value: 'ship.invoiceCode' },
    { label: 'ship.receiptDate', value: 'ship.receiptDate' },
    { label: 'ship.deliveryNote', value: 'ship.deliveryNote' },
    { label: 'ship.customerLedgerId', value: 'ship.customerLedgerId' },
    { label: 'ship.deviceHandlingType', value: 'ship.deviceHandlingType' },
    { label: 'ship.deviceType', value: 'ship.deviceType' },
    { label: 'ship.orderNumber', value: 'ship.orderNumber' },
    { label: 'ship.businessType', value: 'ship.businessType' },
    { label: 'ship.contractType', value: 'ship.contractType' },
    { label: 'ship.isApproved', value: 'ship.isApproved' },
    { label: 'ship.approvedUserId', value: 'ship.approvedUserId' },
    { label: 'ship.approvedDate', value: 'ship.approvedDate' }
  ];

  type DynamicRule = { field: string; operator: string; value: string };
  type DynamicGroup = {
    combinator: 'AND' | 'OR';
    rules: Array<DynamicRule | DynamicGroup>;
  };

  const { group, parentGroup, groupIndex, formRef, formPrefix } = defineProps<{
    group: DynamicGroup;
    parentGroup?: DynamicGroup;
    groupIndex?: number;
    formRef?: FormInstance;
    formPrefix?: string;
  }>();

  const addRule = (group: DynamicGroup) => {
    group.rules.push({ field: '', operator: 'equal', value: '' });
  };
  const addGroup = (group: DynamicGroup) => {
    group.rules.push({ combinator: 'AND', rules: [] });
  };
  const deleteRuleOrGroup = (group: DynamicGroup, idx: number) => {
    group.rules.splice(idx, 1);
  };
  const deleteGroup = (parentGroup: DynamicGroup, groupIndex: number) => {
    parentGroup.rules.splice(groupIndex, 1);
  };
  const toggleCombinator = (group: DynamicGroup) => {
    group.combinator = group.combinator === 'AND' ? 'OR' : 'AND';
  };
</script>

<style scoped lang="scss">
  .builder-group {
    width: inherit;
    border: 1px solid #e0e0e0;
    padding: 12px;
    margin-bottom: 10px;
    background: #fafbfc;
  }
  .builder-group-header {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }
  .builder-rule,
  .builder-nested-group {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }
  .builder-rule :deep(.el-select),
  .builder-rule :deep(.el-input),
  .builder-nested-group :deep(.el-input) {
    height: 32px;
  }
  .is-error {
    border-color: #f56c6c !important;
  }
  .input-error-message {
    color: #f56c6c;
    font-size: 12px;
    margin: 2px 0 0 4px;
    padding: 0;
    line-height: 1.2;
    position: relative;
    z-index: 2;
  }
</style>
